import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "bg-white dark:bg-gray-900 rounded-lg shadow-lg p-4 hover:shadow-xl transition-shadow duration-300" }
const _hoisted_2 = { class: "flex items-center justify-between mb-4" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = { class: "flex items-center space-x-2" }
const _hoisted_5 = { class: "text-lg font-semibold text-gray-900 dark:text-white" }
const _hoisted_6 = {
  key: 0,
  class: "bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center space-x-1 shadow-sm"
}
const _hoisted_7 = { class: "flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_8 = { class: "flex items-center space-x-1" }
const _hoisted_9 = { class: "flex items-center space-x-1" }
const _hoisted_10 = { class: "flex items-center space-x-2" }
const _hoisted_11 = { class: "mb-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700 hidden sm:block" }
const _hoisted_12 = { class: "flex items-center justify-between mb-2" }
const _hoisted_13 = { class: "relative" }
const _hoisted_14 = { class: "text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_15 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center"
}
const _hoisted_16 = {
  key: 1,
  class: "relative transform transition-all duration-500 group-hover:scale-105"
}
const _hoisted_17 = {
  key: 2,
  class: "absolute bottom-4 left-4 right-4 sm:hidden"
}
const _hoisted_18 = { class: "flex justify-center space-x-2" }
const _hoisted_19 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_20 = { class: "flex items-center justify-center space-x-2" }
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "w-10 h-12 bg-gray-100 dark:bg-gray-700 rounded-md overflow-hidden" }
const _hoisted_23 = {
  key: 0,
  class: "absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-md"
}
const _hoisted_24 = { class: "mt-4 text-center" }
const _hoisted_25 = { class: "text-sm text-gray-600 dark:text-gray-300 font-medium" }
const _hoisted_26 = { class: "text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-center space-x-4" }
const _hoisted_27 = { class: "flex items-center space-x-1" }
const _hoisted_28 = {
  key: 0,
  class: "flex items-center space-x-1"
}

import { ref, onMounted, computed } from 'vue'
import { DeviceMockup } from '../../components/ui'
import SimpleSecureImage from '../ui/SimpleSecureImage.vue'
import { isLikelyVideoUrl } from '../../utils/validation'

interface Variation {
  id: string
  imageUrl: string
  thumbnailUrl?: string
  dimensions: {
    width: number
    height: number
  }
  validationScore?: number
  isPrimary: boolean
}

interface Props {
  deviceType: string
  deviceDisplayName: string
  imageUrl: string
  wallpaperId: string // Added wallpaperId for security
  dimensions: { width: number; height: number }
  aspectRatio: string
  hasMultipleVariations: boolean
  isLoading?: boolean
  variations?: Variation[]
  currentVariationIndex?: number
  totalVariations?: number
  fitOption?: string
  // Live wallpaper props
  isLiveWallpaper?: boolean
  videoUrl?: string
  isVideo?: boolean
  isAnimated?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DevicePreview',
  props: {
    deviceType: {},
    deviceDisplayName: {},
    imageUrl: {},
    wallpaperId: {},
    dimensions: {},
    aspectRatio: {},
    hasMultipleVariations: { type: Boolean },
    isLoading: { type: Boolean },
    variations: {},
    currentVariationIndex: {},
    totalVariations: {},
    fitOption: {},
    isLiveWallpaper: { type: Boolean },
    videoUrl: {},
    isVideo: { type: Boolean },
    isAnimated: { type: Boolean }
  },
  emits: ["fullscreen", "share", "preview-click", "touch-start", "touch-move", "touch-end", "select-variation", "fit-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Fit options state
const selectedFit = ref(props.fitOption || 'fill')

// Handle fit option change
const handleFitChange = () => {
  emit('fit-changed', selectedFit.value)
  // Save to localStorage
  localStorage.setItem('wallpaper-fit-preference', selectedFit.value)
}

// Load saved fit preference on mount
onMounted(() => {
  const savedFit = localStorage.getItem('wallpaper-fit-preference')
  if (savedFit && !props.fitOption) {
    selectedFit.value = savedFit
  } else if (props.fitOption) {
    selectedFit.value = props.fitOption
  }
})

// Get fit description for better user understanding
const getFitDescription = (fitOption: string) => {
  const descriptions = {
    fill: 'Image will stretch to completely fill the screen, may crop parts of the image',
    fit: 'Image will scale to fit entirely within the screen, maintaining aspect ratio',
    stretch: 'Image will be distorted to match exact screen dimensions',
    tile: 'Image will repeat in a pattern to fill the entire screen',
    center: 'Image will be centered at original size without scaling',
    span: 'Image will extend across multiple displays as one continuous image'
  }
  return descriptions[fitOption as keyof typeof descriptions] || descriptions.fill
}

// Live wallpaper detection
const isLiveWallpaper = computed(() => {
  // Check explicit flags
  if (props.isVideo || props.isAnimated) return true
  if (props.videoUrl && props.videoUrl.trim().length > 0) return true
  // Also check the imageUrl if it's a video
  return isLikelyVideoUrl(props.imageUrl)
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg shadow-lg" }, [
          _createElementVNode("svg", {
            class: "w-5 h-5 text-white animate-pulse",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
            })
          ])
        ], -1)),
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("h3", _hoisted_5, _toDisplayString(_ctx.deviceDisplayName) + " Preview ", 1),
            (isLiveWallpaper.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[7] || (_cache[7] = [
                  _createElementVNode("span", { class: "relative flex h-1.5 w-1.5" }, [
                    _createElementVNode("span", { class: "animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" }),
                    _createElementVNode("span", { class: "relative inline-flex rounded-full h-1.5 w-1.5 bg-white" })
                  ], -1),
                  _createElementVNode("span", { class: "font-medium" }, "LIVE", -1)
                ])))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _cache[8] || (_cache[8] = _createElementVNode("svg", {
                class: "w-3 h-3",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                })
              ], -1)),
              _createElementVNode("span", null, _toDisplayString(_ctx.dimensions.width) + "×" + _toDisplayString(_ctx.dimensions.height), 1)
            ]),
            _cache[10] || (_cache[10] = _createElementVNode("span", null, "•", -1)),
            _createElementVNode("div", _hoisted_9, [
              _cache[9] || (_cache[9] = _createElementVNode("svg", {
                class: "w-3 h-3",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M4 8V4a1 1 0 011-1h4m12 0h4a1 1 0 011 1v4m0 8v4a1 1 0 01-1 1h-4m-12 0H4a1 1 0 01-1-1v-4"
                })
              ], -1)),
              _createElementVNode("span", null, _toDisplayString(_ctx.aspectRatio), 1)
            ])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('fullscreen'))),
          class: "p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-300 hover:scale-110 active:scale-95 group"
        }, _cache[12] || (_cache[12] = [
          _createElementVNode("svg", {
            class: "w-4 h-4 transform group-hover:scale-110 transition-transform",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            })
          ], -1)
        ])),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('share'))),
          class: "p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400 transition-all duration-300 hover:scale-110 active:scale-95 group"
        }, _cache[13] || (_cache[13] = [
          _createElementVNode("svg", {
            class: "w-4 h-4 transform group-hover:scale-110 transition-transform",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
            })
          ], -1)
        ]))
      ])
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _cache[16] || (_cache[16] = _createElementVNode("div", { class: "flex items-center space-x-2" }, [
          _createElementVNode("svg", {
            class: "w-4 h-4 text-blue-500",
            fill: "none",
            stroke: "currentColor",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            })
          ]),
          _createElementVNode("div", { class: "text-sm font-medium text-gray-700 dark:text-gray-300" }, " Choose how your wallpaper fits ")
        ], -1)),
        _createElementVNode("div", _hoisted_13, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedFit).value = $event)),
            onChange: handleFitChange,
            class: "appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pr-8 text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          }, _cache[14] || (_cache[14] = [
            _createStaticVNode("<option value=\"fill\" data-v-b309e82c>Fill - Stretch to fill screen</option><option value=\"fit\" data-v-b309e82c>Fit - Scale to fit within bounds</option><option value=\"stretch\" data-v-b309e82c>Stretch - Distort to exact size</option><option value=\"tile\" data-v-b309e82c>Tile - Repeat image pattern</option><option value=\"center\" data-v-b309e82c>Center - Center without scaling</option><option value=\"span\" data-v-b309e82c>Span - Extend across displays</option>", 6)
          ]), 544), [
            [_vModelSelect, selectedFit.value]
          ]),
          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" }, [
            _createElementVNode("svg", {
              class: "w-4 h-4 text-gray-400",
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M19 9l-7 7-7-7"
              })
            ])
          ], -1))
        ])
      ]),
      _createElementVNode("div", _hoisted_14, _toDisplayString(getFitDescription(selectedFit.value)), 1)
    ]),
    _createElementVNode("div", {
      class: "relative flex justify-center items-center min-h-[400px] bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden group cursor-pointer",
      onTouchstartPassive: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('touch-start', $event))),
      onTouchmove: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('touch-move', $event))),
      onTouchendPassive: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('touch-end', $event))),
      onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('preview-click')))
    }, [
      _cache[19] || (_cache[19] = _createElementVNode("div", { class: "absolute inset-0 opacity-10" }, [
        _createElementVNode("div", {
          class: "absolute inset-0",
          style: {"background-image":"radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)","background-size":"20px 20px"}
        })
      ], -1)),
      (_ctx.isLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, _cache[17] || (_cache[17] = [
            _createElementVNode("div", { class: "flex flex-col items-center space-y-3" }, [
              _createElementVNode("div", { class: "w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" }),
              _createElementVNode("p", { class: "text-sm text-gray-500 dark:text-gray-400 animate-pulse" }, "Loading preview...")
            ], -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _createVNode(_unref(DeviceMockup), {
              "device-type": _ctx.deviceType,
              wallpaper: _ctx.imageUrl,
              "video-url": isLiveWallpaper.value ? (_ctx.videoUrl || _ctx.imageUrl) : '',
              "wallpaper-id": _ctx.wallpaperId,
              name: _ctx.deviceDisplayName,
              "show-label": false,
              clickable: true,
              "fit-option": selectedFit.value,
              class: "drop-shadow-2xl"
            }, null, 8, ["device-type", "wallpaper", "video-url", "wallpaper-id", "name", "fit-option"]),
            _cache[18] || (_cache[18] = _createElementVNode("div", { class: "absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-end justify-center pb-4" }, [
              _createElementVNode("div", { class: "bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-900 dark:text-white" }, " Click to view full size ")
            ], -1))
          ])),
      (_ctx.hasMultipleVariations)
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
            _createElementVNode("div", _hoisted_18, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.totalVariations, (_, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: _normalizeClass([
              'w-2 h-2 rounded-full transition-all duration-300',
              _ctx.currentVariationIndex === index 
                ? 'bg-white shadow-lg' 
                : 'bg-white/50'
            ])
                }, null, 2))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true)
    ], 32),
    (_ctx.hasMultipleVariations && _ctx.variations && _ctx.variations.length > 1)
      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
          _createElementVNode("div", _hoisted_20, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.variations, (variation, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                onClick: ($event: any) => (_ctx.$emit('select-variation', index)),
                class: _normalizeClass([
            'flex-shrink-0 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden group border-2',
            _ctx.currentVariationIndex === index 
              ? 'border-blue-500 shadow-lg scale-105' 
              : 'border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:scale-110'
          ])
              }, [
                _createElementVNode("div", _hoisted_22, [
                  _createVNode(SimpleSecureImage, {
                    "image-url": variation.thumbnailUrl || variation.imageUrl,
                    "wallpaper-id": _ctx.wallpaperId,
                    "is-preview": true,
                    alt: `${_ctx.deviceDisplayName} Variation ${Number(index) + 1}`,
                    "container-class": 'w-full h-full',
                    "image-class": 'w-full h-full object-cover transition-transform duration-300 group-hover:scale-110',
                    "target-width": 120,
                    "target-height": 144
                  }, null, 8, ["image-url", "wallpaper-id", "alt"])
                ]),
                (_ctx.currentVariationIndex === index)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_23, _cache[20] || (_cache[20] = [
                      _createElementVNode("div", { class: "absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" }, null, -1)
                    ])))
                  : _createCommentVNode("", true),
                _cache[21] || (_cache[21] = _createStaticVNode("<div class=\"absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-md flex items-center justify-center\" data-v-b309e82c><div class=\"opacity-0 group-hover:opacity-100 transition-opacity duration-200\" data-v-b309e82c><svg class=\"w-3 h-3 text-white drop-shadow-lg\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-b309e82c><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M15 12a3 3 0 11-6 0 3 3 0 016 0z\" data-v-b309e82c></path><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z\" data-v-b309e82c></path></svg></div></div>", 1))
              ], 10, _hoisted_21))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_24, [
      _createElementVNode("div", _hoisted_25, _toDisplayString(_ctx.dimensions.width) + " × " + _toDisplayString(_ctx.dimensions.height) + " • " + _toDisplayString(_ctx.aspectRatio) + " aspect ratio ", 1),
      _createElementVNode("div", _hoisted_26, [
        _createElementVNode("div", _hoisted_27, [
          _cache[22] || (_cache[22] = _createElementVNode("svg", {
            class: "w-3 h-3 text-green-500",
            fill: "currentColor",
            viewBox: "0 0 20 20"
          }, [
            _createElementVNode("path", {
              "fill-rule": "evenodd",
              d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
              "clip-rule": "evenodd"
            })
          ], -1)),
          _createElementVNode("span", null, "Optimized for " + _toDisplayString(_ctx.deviceDisplayName), 1)
        ]),
        (_ctx.hasMultipleVariations)
          ? (_openBlock(), _createElementBlock("div", _hoisted_28, _cache[23] || (_cache[23] = [
              _createElementVNode("svg", {
                class: "w-3 h-3 text-blue-500",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                })
              ], -1),
              _createElementVNode("span", { class: "hidden sm:inline" }, "Use arrow keys to navigate", -1),
              _createElementVNode("span", { class: "sm:hidden" }, "Swipe to navigate", -1)
            ])))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})